import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import RadialChart from '../../ValueAcceleration/RadialChart';
import computeVmiScores from '../../../../lib/vmiScoring';

const HOME_VMI_QUERY = gql`
  query HomeVmiData {
    getCurrentUser {
      id
      name
      roles
      diag_readiness_total
      diag_attractiveness_total
      value_gap_current_value
      value_gap_gap_value
      triggering_events { event_type completed_at }
      action_plan {
        items { id status cepaCategory }
        open_count
        in_progress_count
        completed_count
      }
    }
  }
`;

const mapTeStatus = (events = []) => {
  const has = (type) => events.some((e) => String(e?.event_type).toLowerCase() === type && e?.completed_at);
  return {
    identify: has('owner_diagnostic') ? 'complete' : 'missing',
    protect: (has('continuity_plan') || has('catastrophic_plan')) ? 'complete' : 'missing',
    build: (has('value_gap') || has('wealth_gap')) ? 'complete' : 'missing',
    harvest: (has('exit_decision') || has('deal_room')) ? 'partial' : 'missing',
    manage: has('wealth_gap') ? 'partial' : 'missing',
  };
};

const calcCompletionRatios = (items = []) => {
  const init = { identify: { t: 0, d: 0 }, protect: { t: 0, d: 0 }, build: { t: 0, d: 0 }, harvest: { t: 0, d: 0 }, manage: { t: 0, d: 0 } };
  items.forEach((it) => {
    const cat = String(it?.cepaCategory || '').toLowerCase();
    if (!init[cat]) return;
    init[cat].t += 1;
    if (String(it?.status).toLowerCase() === 'completed') init[cat].d += 1;
  });
  const toRatio = (obj) => (obj.t > 0 ? obj.d / obj.t : 0.6);
  return {
    identify: toRatio(init.identify),
    protect: toRatio(init.protect),
    build: toRatio(init.build),
    harvest: toRatio(init.harvest),
    manage: toRatio(init.manage),
  };
};

const AdminScoreTooltip = ({ details }) => {
  const intl = useIntl();
  if (!details) return null;
  return (
    <div className="group relative inline-flex items-center ml-2 align-middle">
      <span className="inline-flex w-5 h-5 items-center justify-center rounded-full border border-succession-border text-[11px] text-succession-text-secondary bg-succession-bg-primary cursor-help">i</span>
      <div className="absolute z-20 top-full mt-2 left-0 w-96 max-w-[90vw] rounded-md border border-succession-border bg-succession-bg-primary shadow-succession-md p-3 text-xs text-succession-text-secondary opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
        <div className="font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.vmi_panel.score_details' })}</div>
        {details.map((d) => (
          <div key={d.id} className="mb-2">
            <div className="flex items-center justify-between">
              <span className="text-succession-text-primary font-medium">{d.label}</span>
              <span className="text-succession-text-primary">{d.score} / 6</span>
            </div>
            <div className="mt-1 leading-snug space-y-0.5">
              <div>
                <span className="text-succession-text-primary">Base:</span> {(d.base6).toFixed(2)} = 6 × (wᵣ×r + wₐ×a)
                <div className="ml-2 text-[11px]">
                  r = norm(readiness={d.readinessTotal ?? '∅'}) → {d.r.toFixed(2)}, a = norm(attractiveness={d.attractivenessTotal ?? '∅'}) → {d.a.toFixed(2)}
                </div>
                <div className="ml-2 text-[11px]">wᵣ={Math.round(d.weights.r*100)}%, wₐ={Math.round(d.weights.a*100)}%</div>
              </div>
              <div>
                <span className="text-succession-text-primary">Tasks:</span> × {d.taskMultiplier.toFixed(2)} = 0.6 + 0.4×ratio
                <div className="ml-2 text-[11px]">completed {d.done}/{d.total} → ratio {d.ratio.toFixed(2)} {d.cappedAt === 5 ? '(cap 5 due to open tasks)' : ''}</div>
              </div>
              <div>
                <span className="text-succession-text-primary">Triggering Events:</span> × {d.teMultiplier.toFixed(2)} ({d.teStatus})
                {d.teNote && <div className="ml-2 text-[11px]">{d.teNote}</div>}
              </div>
              {d.vgMultiplier != null && (
                <div>
                  <span className="text-succession-text-primary">Value Gap:</span> × {d.vgMultiplier.toFixed(2)} = 1 − min(0.4, gap/current)
                  <div className="ml-2 text-[11px]">current={Number.isFinite(d.currentValue) ? d.currentValue.toFixed(0) : '∅'}, gap={Number.isFinite(d.gapValue) ? d.gapValue.toFixed(0) : '∅'}, ratio={(d.vgRatio ?? 0).toFixed(2)}{d.vgCapped ? ' (capped)' : ''}</div>
                </div>
              )}
              <div>
                <span className="text-succession-text-primary">Raw:</span> {(d.raw).toFixed(2)} → rounded to nearest 0.5 ⇒ {d.score.toFixed(1)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HomeVmiPanel = () => {
  const { data, loading, error, refetch } = useQuery(HOME_VMI_QUERY, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    const handler = () => { try { refetch(); } catch(_) {} };
    try {
      window.addEventListener('action-plan-updated', handler);
      window.addEventListener('owner-diagnostic-updated', handler);
      window.addEventListener('value-gap-updated', handler);
    } catch(_) {}
    return () => {
      try {
        window.removeEventListener('action-plan-updated', handler);
        window.removeEventListener('owner-diagnostic-updated', handler);
        window.removeEventListener('value-gap-updated', handler);
      } catch(_) {}
    };
  }, [refetch]);

  if (loading) return <div className="h-56 md:h-60 animate-pulse bg-succession-bg-secondary rounded-md" aria-hidden="true" />;
  if (error) return <div className="text-xs text-succession-error"><FormattedMessage id="succession.vmi_panel.error" /></div>;

  const u = data?.getCurrentUser || {};
  const hasAnyCompleted = Array.isArray(u?.triggering_events) && u.triggering_events.some((e) => e && e.completed_at);

  if (!hasAnyCompleted) {
    return (
      <div className="flex-1 min-h-[200px] flex flex-col items-center justify-center text-center">
        <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.vmi_panel.empty_title" /></h3>
        <h4 className="text-sm text-succession-text-secondary max-w-xl mx-auto mt-1">
          <FormattedMessage id="succession.vmi_panel.empty_body" />
        </h4>
        <div className="mt-3">
          <button
            type="button"
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm"
            onClick={() => { try { if (window.ahoy?.track) window.ahoy.track('succession_readiness_start_assessment_clicked'); } catch(_) {} window.location.href = '/succession/plans'; }}
          >
            <FormattedMessage id="succession.vmi_panel.start_assessment" />
          </button>
        </div>
      </div>
    );
  }
  const isAdmin = Array.isArray(u?.roles) && u.roles.includes('admin');
  const completionRatios = calcCompletionRatios(u?.action_plan?.items || []);
  const teStatus = mapTeStatus(u?.triggering_events || []);
  const valueGap = { currentValue: u?.value_gap_current_value, gapValue: u?.value_gap_gap_value };

  // Build a detailed breakdown alongside the display data
  const weights = {
    identify: { r: 0.7, a: 0.3 },
    protect:  { r: 0.8, a: 0.2 },
    build:    { r: 0.3, a: 0.7 },
    harvest:  { r: 0.5, a: 0.5 },
    manage:   { r: 0.6, a: 0.4 },
  };

  const r = (() => { const x = u?.diag_readiness_total; if (x == null) return 0.4; const v = (Number(x) - 6) / 24; return Math.max(0, Math.min(1, v)); })();
  const a = (() => { const x = u?.diag_attractiveness_total; if (x == null) return 0.4; const v = (Number(x) - 6) / 24; return Math.max(0, Math.min(1, v)); })();

  const base = {
    identify: weights.identify.r * r + weights.identify.a * a,
    protect:  weights.protect.r  * r + weights.protect.a  * a,
    build:    weights.build.r    * r + weights.build.a    * a,
    harvest:  weights.harvest.r  * r + weights.harvest.a  * a,
    manage:   weights.manage.r   * r + weights.manage.a   * a,
  };

  const teMult = {
    identify: teStatus.identify === 'complete' ? 1.0 : teStatus.identify === 'partial' ? 0.9 : 0.9,
    protect:  teStatus.protect  === 'complete' ? 1.0 : teStatus.protect  === 'partial' ? 0.9 : 0.9,
    build:    teStatus.build    === 'complete' ? 1.0 : teStatus.build    === 'partial' ? 0.9 : 0.9,
    harvest:  teStatus.harvest  === 'complete' ? 1.0 : teStatus.harvest  === 'partial' ? 0.9 : 0.9,
    manage:   teStatus.manage   === 'complete' ? 1.0 : teStatus.manage   === 'partial' ? 0.9 : 0.9,
  };

  const taskMult = {
    identify: 0.6 + 0.4 * (completionRatios.identify ?? 0.6),
    protect:  0.6 + 0.4 * (completionRatios.protect  ?? 0.6),
    build:    0.6 + 0.4 * (completionRatios.build    ?? 0.6),
    harvest:  0.6 + 0.4 * (completionRatios.harvest  ?? 0.6),
    manage:   0.6 + 0.4 * (completionRatios.manage   ?? 0.6),
  };
  const caps = {
    identify: (completionRatios.identify ?? 0) < 1 ? 5 : 6,
    protect:  (completionRatios.protect  ?? 0) < 1 ? 5 : 6,
    build:    (completionRatios.build    ?? 0) < 1 ? 5 : 6,
    harvest:  (completionRatios.harvest  ?? 0) < 1 ? 5 : 6,
    manage:   (completionRatios.manage   ?? 0) < 1 ? 5 : 6,
  };
  const vgMult = {
    identify: 1,
    protect:  1,
    build:    (() => { const cv = Number(valueGap.currentValue||0); const gv = Number(valueGap.gapValue||0); return cv>0 ? (1 - Math.max(0, Math.min(0.4, gv/cv))) : 1; })(),
    harvest:  (() => { const cv = Number(valueGap.currentValue||0); const gv = Number(valueGap.gapValue||0); return cv>0 ? (1 - Math.max(0, Math.min(0.4, gv/cv))) : 1; })(),
    manage:   1,
  };

  const result = computeVmiScores({
    readinessTotal: u?.diag_readiness_total,
    attractivenessTotal: u?.diag_attractiveness_total,
    completionRatios,
    teStatus,
    valueGap,
  });

  const eventSet = Array.isArray(u?.triggering_events) ? u.triggering_events : [];
  const teCompleted = (type) => eventSet.some((e) => String(e?.event_type).toLowerCase() === type && e?.completed_at);
  const teNotes = {
    identify: teCompleted('owner_diagnostic') ? 'owner_diagnostic completed' : 'owner_diagnostic missing',
    protect: (teCompleted('continuity_plan') || teCompleted('catastrophic_plan')) ? 'continuity/catastrophic plan complete' : 'no continuity/catastrophic plan',
    build: (teCompleted('value_gap') || teCompleted('wealth_gap')) ? 'value/wealth gap complete' : 'no value/wealth gap',
    harvest: (teCompleted('exit_decision') || teCompleted('deal_room')) ? 'exit/deal room activity recorded' : 'no exit/deal room activity',
    manage: teCompleted('wealth_gap') ? 'wealth gap provides manage signal' : 'no manage signal',
  };

  const buildDetail = (id, label) => {
    const base6 = base[id] * 6;
    const tm = taskMult[id];
    const tem = teMult[id];
    const vgm = (id === 'build' || id === 'harvest') ? vgMult[id] : null;
    const raw = base6 * tm * tem * (vgm != null ? vgm : 1);
    const score = result.categories.find((c) => c.id === id)?.score;
    const count = {
      total: (u?.action_plan?.items || []).filter((it) => String(it?.cepaCategory || '').toLowerCase() === id).length,
      done: (u?.action_plan?.items || []).filter((it) => String(it?.cepaCategory || '').toLowerCase() === id && String(it?.status).toLowerCase() === 'completed').length,
    };
    const ratio = count.total > 0 ? count.done / count.total : 0.6;
    const vgRatio = (() => { const cv = Number(valueGap.currentValue||0); const gv = Number(valueGap.gapValue||0); return cv>0 ? Math.min(0.4, Math.max(0, gv/cv)) : 0; })();
    const vgCapped = vgRatio >= 0.4 - 1e-9; // approximately capped
    return {
      id, label,
      base6, base: base[id], weights: weights[id],
      r, a, readinessTotal: u?.diag_readiness_total, attractivenessTotal: u?.diag_attractiveness_total,
      taskMultiplier: tm, teMultiplier: tem, vgMultiplier: vgm,
      cappedAt: caps[id],
      done: count.done, total: count.total, ratio,
      teStatus: teStatus[id], teNote: teNotes[id],
      currentValue: valueGap.currentValue, gapValue: valueGap.gapValue, vgRatio, vgCapped,
      raw, score,
    };
  };

  const details = [
    buildDetail('identify', 'Identify'),
    buildDetail('protect', 'Protect'),
    buildDetail('build', 'Build'),
    buildDetail('harvest', 'Harvest'),
    buildDetail('manage', 'Manage'),
  ];

  const chartData = result.categories.map((c) => ({ subject: c.label, A: c.score, fullMark: 6 }));

  return (
    <div className="flex-1 min-h-[140px] flex items-start justify-start">
      <div className="w-full">
        <div className="origin-top-left md:origin-top-left">
          <RadialChart
            title={(
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <span className="text-[1.5em] leading-tight">{result.overallPercent}%</span>
                  <span className="font-normal text-xs md:text-sm text-succession-text-secondary"><FormattedMessage id="succession.vmi_panel.overall_readiness" /></span>
                </div>
                {isAdmin && <AdminScoreTooltip details={details} />}
              </div>
            )}
            data={chartData}
            height={420}
          />
        </div>
      </div>
    </div>
  );
};

export default HomeVmiPanel;




